import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../context/AuthContext';
import { useWidgetVisibility } from '../context/WidgetVisibilityContext';
import {
  bedService,
  expenseService,
  floorService,
  creditsService,
  hostelService,
  invoiceService,
  meService,
  roomService,
  tenantService,
} from '../services/api';
import { Bed, DailyGuest, Expense, Floor, Hostel, Invoice, Room, User } from '../types';
import {
  dateOnlyFromApi,
  guestBalanceDue,
  isActiveDailyGuest,
  toYmd,
} from '../utils/dailyGuestDates';
import {
  MONTH_SHORT,
  monthBounds,
  sumCollectionForPeriod,
  sumExpensesForPeriod,
} from '../utils/dashboardFinance';
import { computeBedOccupancyFromList } from '../utils/bedOccupancy';
import { formatFloorCardLabel } from '../utils/locationLabels';
import { filterResidentTenants } from '../utils/residentTenants';
import { isActiveTenant, isTenantOnVacatingNotice } from '../utils/tenantStatus';
import {
  buildFloorRoomLookup,
  computeMonthlyExpectedCollection,
  computePendingPaymentsSummary,
  formatINR,
  groupInvoicesByUserId,
  tenantDisplayName,
  tenantLocation,
} from './reports/reportsUtils';
import PhoneLink from '../components/PhoneLink';
import TenantNameLink from '../components/TenantNameLink';
import HostelCreditsCell from '../components/HostelCreditsCell';
import LoadHostelCreditsModal from '../components/LoadHostelCreditsModal';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

type HostelAnalyticsPayload = {
  occupancy: {
    totalBeds: number;
    occupiedBeds: number;
    availableBeds: number;
    occupancyRate: string;
  };
  revenue: { totalRevenue: number; totalDues: number };
  tenants: { activeTenants: number };
  complaints: { openComplaints: number; resolvedComplaints: number };
};

type SuperHostelRow = {
  hostel: Hostel;
  analytics: HostelAnalyticsPayload | null;
  error?: string;
};

type SuperAggregates = {
  hostelCount: number;
  totalBeds: number;
  occupiedBeds: number;
  activeTenants: number;
  totalRevenue: number;
  totalDues: number;
  openComplaints: number;
};

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

const floorLabel = formatFloorCardLabel;

const Dashboard: React.FC = () => {
  const { user, hostel, isAdmin, isSuperAdmin, isTenant, isGuest, isResident } = useAuth();
  const { isVisible } = useWidgetVisibility();
  const showPropertyDashboard = !isSuperAdmin() && !isResident();

  const dashboardYear = new Date().getFullYear();

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalInvoices: 0,
    totalRevenue: 0,
    pendingPayments: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [superRows, setSuperRows] = useState<SuperHostelRow[]>([]);
  const [superAggregates, setSuperAggregates] = useState<SuperAggregates | null>(null);

  const [analytics, setAnalytics] = useState<HostelAnalyticsPayload | null>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [tenants, setTenants] = useState<User[]>([]);
  const [yearExpenses, setYearExpenses] = useState<Expense[]>([]);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [dailyGuests, setDailyGuests] = useState<DailyGuest[]>([]);
  const [guestCheckoutBusyId, setGuestCheckoutBusyId] = useState<number | null>(null);
  const [propertyCredits, setPropertyCredits] = useState<{ emailCredits: number; whatsappCredits: number } | null>(
    null,
  );
  const [creditsHostel, setCreditsHostel] = useState<Hostel | null>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostel?.id]);

  useEffect(() => {
    if (!showPropertyDashboard || !hostel?.id) {
      setPropertyCredits(null);
      return;
    }
    let cancelled = false;
    void creditsService.getHostelCredits(hostel.id).then((data) => {
      if (!cancelled) {
        setPropertyCredits({
          emailCredits: data.emailCredits,
          whatsappCredits: data.whatsappCredits,
        });
      }
    }).catch(() => {
      if (!cancelled) setPropertyCredits(null);
    });
    return () => {
      cancelled = true;
    };
  }, [showPropertyDashboard, hostel?.id]);

  const loadDashboardData = async () => {
    if (!hostel?.id && !isSuperAdmin()) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      if (isSuperAdmin()) {
        const hostels = await hostelService.getAllHostels();
        const active = hostels.filter((h) => !h.isDeleted);
        const results = await Promise.allSettled(active.map((h) => hostelService.getHostelAnalytics(h.id)));

        const rows: SuperHostelRow[] = active.map((h, i) => {
          const r = results[i];
          if (r.status === 'fulfilled') {
            return { hostel: h, analytics: r.value as HostelAnalyticsPayload };
          }
          const msg =
            (r.reason as { response?: { data?: { message?: string } }; message?: string })?.response?.data
              ?.message ||
            (r.reason as Error)?.message ||
            'Unable to load analytics';
          return { hostel: h, analytics: null, error: msg };
        });

        setSuperRows(rows);

        const agg: SuperAggregates = {
          hostelCount: rows.length,
          totalBeds: 0,
          occupiedBeds: 0,
          activeTenants: 0,
          totalRevenue: 0,
          totalDues: 0,
          openComplaints: 0,
        };
        for (const row of rows) {
          if (!row.analytics) continue;
          agg.totalBeds += num(row.analytics.occupancy.totalBeds);
          agg.occupiedBeds += num(row.analytics.occupancy.occupiedBeds);
          agg.activeTenants += num(row.analytics.tenants.activeTenants);
          agg.totalRevenue += num(row.analytics.revenue.totalRevenue);
          agg.totalDues += num(row.analytics.revenue.totalDues);
          agg.openComplaints += num(row.analytics.complaints.openComplaints);
        }
        setSuperAggregates(agg);
      } else if (showPropertyDashboard && hostel?.id) {
        const hid = hostel.id;
        const year = dashboardYear;
        const [analyticsRes, floorsRes, roomsRes, bedsRes, tenantsRes, expensesRes, invoicesRes, guestsRes] =
          await Promise.all([
          hostelService.getHostelAnalytics(hid),
          floorService.getFloorsByHostel(hid),
          roomService.getRoomsByHostel(hid),
          bedService.getBedsByHostel(hid),
          tenantService.getTenantsByHostel(hid, 'ALL_USERS'),
          expenseService.getExpensesByHostel(hid, {
            startDate: `${year}-01-01`,
            endDate: `${year}-12-31`,
          }),
          invoiceService.getAllInvoices(),
          tenantService.getDailyGuestsByHostel(hid, { status: 'ACTIVE' }),
        ]);

        setAnalytics(analyticsRes as HostelAnalyticsPayload);
        setFloors(floorsRes.filter((f) => f.isActive !== false));
        setRooms(roomsRes.filter((r) => r.isActive !== false));
        setBeds(bedsRes.filter((b) => b.isActive !== false));
        setTenants(filterResidentTenants(tenantsRes));
        setYearExpenses(expensesRes);
        setAllInvoices(invoicesRes);
        setDailyGuests(guestsRes);

        if (isAdmin()) {
          const userCount = await hostelService.getHostelUserCount(hid);
          setStats((s) => ({ ...s, totalUsers: typeof userCount === 'number' ? userCount : 0 }));
        }
      } else if (isGuest()) {
        const account = await meService.getAccount();
        const stays = await meService.getGuestStays();
        const stay = account.guestStay;
        const totalPaid = stays.reduce((sum, s) => sum + Number(s.amountPaid || 0), 0);
        const totalPending = stay?.balanceDue != null ? Number(stay.balanceDue) : 0;
        setStats({
          totalUsers: 0,
          totalInvoices: stays.length,
          totalRevenue: totalPaid,
          pendingPayments: totalPending,
        });
        setRecentInvoices([]);
      } else if (isTenant()) {
        const invoices = await invoiceService.getUserInvoices(user?.id || 0);
        const totalPaid = invoices.reduce((sum, inv) => sum + parseFloat(String(inv.paidAmount || 0)), 0);
        const totalPending = invoices.reduce((sum, inv) => sum + parseFloat(String(inv.balanceAmount || 0)), 0);
        setStats({
          totalUsers: 0,
          totalInvoices: invoices.length,
          totalRevenue: totalPaid,
          pendingPayments: totalPending,
        });
        setRecentInvoices(invoices.slice(-5).reverse());
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (isSuperAdmin()) {
        setSuperRows([]);
        setSuperAggregates({
          hostelCount: 0,
          totalBeds: 0,
          occupiedBeds: 0,
          activeTenants: 0,
          totalRevenue: 0,
          totalDues: 0,
          openComplaints: 0,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const kpi = useMemo(() => {
    const occ = computeBedOccupancyFromList(beds);
    const noticeBeds = tenants.filter(isTenantOnVacatingNotice).length;
    return {
      totalBeds: occ.totalBeds,
      occupiedBeds: occ.occupiedBeds,
      availableBeds: occ.availableBeds,
      noticeBeds,
      occPct: occ.occupancyPct,
    };
  }, [beds, tenants]);

  const monthlyChart = useMemo(() => {
    if (!hostel?.id) return { labels: MONTH_SHORT, collection: [] as number[], expense: [] as number[] };
    const year = dashboardYear;
    const collection: number[] = [];
    const expense: number[] = [];
    for (let m = 1; m <= 12; m++) {
      const { start, end } = monthBounds(year, m);
      collection.push(sumCollectionForPeriod(allInvoices, hostel.id, start, end));
      expense.push(sumExpensesForPeriod(yearExpenses, start, end));
    }
    return { labels: MONTH_SHORT, collection, expense };
  }, [hostel?.id, dashboardYear, allInvoices, yearExpenses]);

  const growthChartData = useMemo(
    () => ({
      labels: monthlyChart.labels,
      datasets: [
        {
          label: 'Collection',
          data: monthlyChart.collection,
          backgroundColor: 'rgba(37, 99, 235, 0.85)',
          borderRadius: 6,
          barPercentage: 0.72,
          categoryPercentage: 0.65,
        },
        {
          label: 'Expense',
          data: monthlyChart.expense,
          backgroundColor: 'rgba(249, 115, 22, 0.85)',
          borderRadius: 6,
          barPercentage: 0.72,
          categoryPercentage: 0.65,
        },
      ],
    }),
    [monthlyChart],
  );

  const emptyBedsByFloor = useMemo(() => {
    const grouped: Record<number, Bed[]> = {};
    beds.forEach((b) => {
      const fid = Number(b.floorId);
      if (!grouped[fid]) grouped[fid] = [];
      grouped[fid].push(b);
    });
    const list = floors.length > 0 ? floors : [];
    return list
      .slice()
      .sort((a, b) =>
        String(a.floorNumber).localeCompare(String(b.floorNumber), undefined, { numeric: true }),
      )
      .map((f) => {
        const floorBeds = grouped[f.id] || [];
        const empty = floorBeds.filter((b) => String(b.bedStatus).toUpperCase() !== 'OCCUPIED').length;
        return { floor: f, empty, total: floorBeds.length };
      });
  }, [floors, beds]);

  const formatDisplayDate = (val: string | undefined | null) => {
    if (!val) return '—';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const joinedThisMonthLabel = useMemo(() => {
    const now = new Date();
    return now.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }, []);

  const joinedThisMonthUsers = useMemo(() => {
    const now = new Date();
    const { start, end } = monthBounds(now.getFullYear(), now.getMonth() + 1);
    const rangeStart = new Date(start);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(end);
    rangeEnd.setHours(23, 59, 59, 999);

    return tenants
      .filter((t) => {
        if (t.isDeleted) return false;
        if (!t.dateOfJoining) return false;
        const joined = new Date(t.dateOfJoining);
        if (Number.isNaN(joined.getTime())) return false;
        return joined.getTime() >= rangeStart.getTime() && joined.getTime() <= rangeEnd.getTime();
      })
      .sort((a, b) => new Date(b.dateOfJoining!).getTime() - new Date(a.dateOfJoining!).getTime())
      .slice(0, 8)
      .map((t) => ({
        id: t.id,
        name: t.username || t.phoneNumber || 'Tenant',
        phone: t.phoneNumber || '',
        joined: formatDisplayDate(t.dateOfJoining),
      }));
  }, [tenants]);

  const todayYmd = useMemo(() => toYmd(new Date()), []);

  const upcomingVacatingUsers = useMemo(() => {
    return tenants
      .filter((t) => {
        if (!isTenantOnVacatingNotice(t)) return false;
        const leaving = dateOnlyFromApi(t.leavingDate);
        return leaving !== todayYmd;
      })
      .sort((a, b) => {
        const ta = a.leavingDate ? new Date(a.leavingDate).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.leavingDate ? new Date(b.leavingDate).getTime() : Number.POSITIVE_INFINITY;
        return ta - tb;
      })
      .slice(0, 8)
      .map((t) => ({
        id: t.id,
        name: t.username || t.phoneNumber || 'Tenant',
        leaving: formatDisplayDate(t.leavingDate),
        notified: formatDisplayDate(t.notifiedDate),
      }));
  }, [tenants, todayYmd]);

  const tenantsVacatingToday = useMemo(() => {
    return tenants
      .filter(
        (t) => isTenantOnVacatingNotice(t) && dateOnlyFromApi(t.leavingDate) === todayYmd,
      )
      .slice(0, 8)
      .map((t) => ({
        id: t.id,
        name: t.username || t.phoneNumber || 'Tenant',
        leaving: formatDisplayDate(t.leavingDate),
        notified: formatDisplayDate(t.notifiedDate),
      }));
  }, [tenants, todayYmd]);

  const paidUsersPie = useMemo(() => {
    const active = tenants.filter(isActiveTenant);
    const now = new Date();
    const { start, end } = monthBounds(now.getFullYear(), now.getMonth() + 1);
    const paidIds = new Set<number>();
    if (hostel?.id) {
      allInvoices.forEach((inv) => {
        if (inv.isDeleted) return;
        if (inv.hostelId != null && Number(inv.hostelId) !== hostel.id) return;
        const paid = parseFloat(String(inv.paidAmount || 0));
        if (paid <= 0) return;
        const ts = inv.paymentTimestamp ? new Date(inv.paymentTimestamp).getTime() : NaN;
        if (!Number.isFinite(ts)) return;
        const s = new Date(start);
        s.setHours(0, 0, 0, 0);
        const e = new Date(end);
        e.setHours(23, 59, 59, 999);
        if (ts >= s.getTime() && ts <= e.getTime() && inv.userId) paidIds.add(Number(inv.userId));
      });
    }
    const paid = active.filter((t) => paidIds.has(t.id)).length;
    const pending = Math.max(0, active.length - paid);
    return { paid, pending, total: active.length };
  }, [tenants, allInvoices, hostel?.id]);

  const bookingPieData = useMemo(
    () => ({
      labels: ['Paid this month', 'Not paid yet'],
      datasets: [
        {
          data: [paidUsersPie.paid, paidUsersPie.pending],
          backgroundColor: ['rgba(16, 185, 129, 0.9)', 'rgba(226, 232, 240, 0.95)'],
          borderColor: ['#fff', '#fff'],
          borderWidth: 2,
        },
      ],
    }),
    [paidUsersPie],
  );

  const invoicesByUser = useMemo(() => {
    if (!hostel?.id) return new Map<number, Invoice[]>();
    const hid = hostel.id;
    return groupInvoicesByUserId(
      allInvoices.filter((i) => !i.isDeleted && Number(i.hostelId) === hid),
    );
  }, [allInvoices, hostel?.id]);

  const locationLookup = useMemo(() => buildFloorRoomLookup(floors, rooms), [floors, rooms]);

  const mapGuestRow = useCallback(
    (g: DailyGuest) => {
      const balance = guestBalanceDue(g);
      const loc =
        g.floorId != null || g.roomId != null
          ? tenantLocation(
              {
                floor: g.floorId != null ? String(g.floorId) : undefined,
                room: g.roomId != null ? String(g.roomId) : undefined,
                bed: g.bedId != null ? String(g.bedId) : undefined,
              } as User,
              locationLookup,
            )
          : '—';
      return {
        guest: g,
        id: g.id,
        name: g.name,
        phone: g.phoneNumber,
        checkIn: formatDisplayDate(g.checkInDate),
        checkOut: formatDisplayDate(g.checkOutDate),
        location: loc,
        balance,
      };
    },
    [locationLookup],
  );

  const guestsCheckingOutToday = useMemo(() => {
    return dailyGuests
      .filter((g) => isActiveDailyGuest(g) && dateOnlyFromApi(g.checkOutDate) === todayYmd)
      .map(mapGuestRow);
  }, [dailyGuests, todayYmd, mapGuestRow]);

  const guestsCheckingInToday = useMemo(() => {
    return dailyGuests
      .filter((g) => isActiveDailyGuest(g) && dateOnlyFromApi(g.checkInDate) === todayYmd)
      .map(mapGuestRow);
  }, [dailyGuests, todayYmd, mapGuestRow]);

  const upcomingGuestCheckouts = useMemo(() => {
    const today = new Date(todayYmd);
    today.setHours(0, 0, 0, 0);
    const horizon = new Date(today);
    horizon.setDate(horizon.getDate() + 14);

    return dailyGuests
      .filter((g) => {
        if (!isActiveDailyGuest(g)) return false;
        const out = dateOnlyFromApi(g.checkOutDate);
        if (!out || out === todayYmd) return false;
        const d = new Date(out);
        if (Number.isNaN(d.getTime())) return false;
        d.setHours(0, 0, 0, 0);
        return d > today && d <= horizon;
      })
      .sort((a, b) => dateOnlyFromApi(a.checkOutDate).localeCompare(dateOnlyFromApi(b.checkOutDate)))
      .slice(0, 8)
      .map(mapGuestRow);
  }, [dailyGuests, todayYmd, mapGuestRow]);

  const handleGuestCheckout = async (g: DailyGuest) => {
    const balance = guestBalanceDue(g);
    const ok = window.confirm(
      balance > 0
        ? `${g.name} has ₹${balance.toLocaleString('en-IN')} balance due. Mark as checked out anyway?`
        : `Check out ${g.name}?`,
    );
    if (!ok) return;
    try {
      setGuestCheckoutBusyId(g.id);
      await tenantService.updateDailyGuest(g.id, { status: 'COMPLETED' });
      setDailyGuests((prev) => prev.filter((row) => row.id !== g.id));
      toast.success(`${g.name} checked out`);
    } catch {
      toast.error('Could not check out guest');
    } finally {
      setGuestCheckoutBusyId(null);
    }
  };

  const pendingPayments = useMemo(
    () => computePendingPaymentsSummary(tenants.filter(isActiveTenant), invoicesByUser, 8),
    [tenants, invoicesByUser],
  );

  const currentMonthPeriod = useMemo(() => {
    const now = new Date();
    return monthBounds(now.getFullYear(), now.getMonth() + 1);
  }, []);

  const monthCollection = useMemo(() => {
    if (!hostel?.id) return 0;
    const { start, end } = currentMonthPeriod;
    return sumCollectionForPeriod(allInvoices, hostel.id, start, end);
  }, [allInvoices, hostel?.id, currentMonthPeriod]);

  const monthExpense = useMemo(() => {
    const { start, end } = currentMonthPeriod;
    return sumExpensesForPeriod(yearExpenses, start, end);
  }, [yearExpenses, currentMonthPeriod]);

  const monthlyExpected = useMemo(
    () => computeMonthlyExpectedCollection(tenants),
    [tenants],
  );

  if (loading) {
    return <div className="loading">Loading dashboard…</div>;
  }

  if (isSuperAdmin() && superAggregates != null) {
    const occPct =
      superAggregates.totalBeds > 0
        ? ((superAggregates.occupiedBeds / superAggregates.totalBeds) * 100).toFixed(1)
        : '0';

    return (
      <div className="dashboard sa-dashboard">
        <div className="sa-dashboard-hero">
          <div>
            <h1 className="dashboard-title">Platform overview</h1>
            <p className="sa-dashboard-subtitle">
              Live metrics per hostel — occupancy, tenants, collected rent, outstanding dues, and complaints.
            </p>
          </div>
          <div className="sa-dashboard-hero-actions">
            <Link to="/settings" className="btn btn-secondary">
              Settings
            </Link>
            <Link to="/hostels" className="btn btn-primary">
              Manage hostels
            </Link>
          </div>
        </div>

        <div className="stats-grid">
          {isVisible('super.stat_hostels') && (
            <div className="stat-card sa-stat-accent">
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>Active hostels</h3>
                <p className="stat-value">{superAggregates.hostelCount}</p>
              </div>
            </div>
          )}
          {isVisible('super.stat_beds') && (
            <div className="stat-card">
              <div className="stat-icon">🛏️</div>
              <div className="stat-content">
                <h3>Beds (occupied / total)</h3>
                <p className="stat-value">
                  {superAggregates.occupiedBeds} / {superAggregates.totalBeds}
                </p>
                <p className="sa-stat-foot">{occPct}% occupied</p>
              </div>
            </div>
          )}
          {isVisible('super.stat_tenants') && (
            <div className="stat-card">
              <div className="stat-icon">🧑‍🤝‍🧑</div>
              <div className="stat-content">
                <h3>Active tenants</h3>
                <p className="stat-value">{superAggregates.activeTenants}</p>
              </div>
            </div>
          )}
          {isVisible('super.stat_revenue') && (
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>Paid invoice total</h3>
                <p className="stat-value">₹{superAggregates.totalRevenue.toLocaleString()}</p>
                <p className="sa-stat-foot">Across all hostels (PAID)</p>
              </div>
            </div>
          )}
          {isVisible('super.stat_dues') && (
            <div className="stat-card">
              <div className="stat-icon">📉</div>
              <div className="stat-content">
                <h3>Outstanding dues</h3>
                <p className="stat-value">₹{superAggregates.totalDues.toLocaleString()}</p>
              </div>
            </div>
          )}
          {isVisible('super.stat_complaints') && (
            <div className="stat-card">
              <div className="stat-icon">⚠️</div>
              <div className="stat-content">
                <h3>Open complaints</h3>
                <p className="stat-value">{superAggregates.openComplaints}</p>
              </div>
            </div>
          )}
        </div>

        {isVisible('super.hostel_table') && (
        <div className="recent-section sa-hostel-section">
          <h2>Hostel breakdown</h2>
          <p className="sa-agg-note">Each row is one property. Values come from the same analytics used on the hostel detail view.</p>
          <div className="invoices-table sa-hostel-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Hostel</th>
                  <th>Code</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Beds</th>
                  <th>Occ.</th>
                  <th>Tenants</th>
                  <th>Paid (₹)</th>
                  <th>Dues (₹)</th>
                  <th>Complaints</th>
                  <th>Credits</th>
                </tr>
              </thead>
              <tbody>
                {superRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="no-data">
                      No active hostels yet. <Link to="/hostels">Create or restore a hostel</Link>.
                    </td>
                  </tr>
                ) : (
                  superRows.map((row) => {
                    const h = row.hostel;
                    const a = row.analytics;
                    const cityLine = [h.city, h.state].filter(Boolean).join(', ') || '—';
                    return (
                      <tr key={h.id}>
                        <td>
                          <div className="sa-hostel-name">{h.name}</div>
                          {row.error && <div className="sa-hostel-err">{row.error}</div>}
                        </td>
                        <td>{h.hostelCode ? <span className="sa-code-pill">{h.hostelCode}</span> : '—'}</td>
                        <td>{cityLine}</td>
                        <td>
                          <span className={`status-badge ${(h.status || 'ACTIVE').toLowerCase()}`}>
                            {h.status || 'ACTIVE'}
                          </span>
                        </td>
                        <td>{h.subscriptionPlan || '—'}</td>
                        <td>{a ? `${a.occupancy.occupiedBeds} / ${a.occupancy.totalBeds}` : '—'}</td>
                        <td>{a ? a.occupancy.occupancyRate : '—'}</td>
                        <td>{a ? a.tenants.activeTenants : '—'}</td>
                        <td>{a ? num(a.revenue.totalRevenue).toLocaleString() : '—'}</td>
                        <td>{a ? num(a.revenue.totalDues).toLocaleString() : '—'}</td>
                        <td>
                          {a ? (
                            <span>
                              {a.complaints.openComplaints} open / {a.complaints.resolvedComplaints} resolved
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <div className="sa-credits-cell">
                            <HostelCreditsCell
                              emailCredits={h.emailCredits}
                              whatsappCredits={h.whatsappCredits}
                            />
                            <button
                              type="button"
                              className="btn btn-xs btn-secondary sa-load-credits-btn"
                              onClick={() => setCreditsHostel(h)}
                            >
                              Load
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {creditsHostel && (
          <LoadHostelCreditsModal
            hostel={creditsHostel}
            onClose={() => setCreditsHostel(null)}
            onSuccess={(credits) => {
              const hid = creditsHostel.id;
              if (!hid) return;
              setSuperRows((prev) =>
                prev.map((row) =>
                  row.hostel.id === hid
                    ? {
                        ...row,
                        hostel: {
                          ...row.hostel,
                          emailCredits: credits.emailCredits,
                          whatsappCredits: credits.whatsappCredits,
                        },
                      }
                    : row,
                ),
              );
            }}
          />
        )}
      </div>
    );
  }

  if (showPropertyDashboard && hostel) {
    return (
      <div className="hd-dashboard">
        <header className="hd-dashboard-header">
          <div>
            <h1 className="hd-dashboard-title">Dashboard</h1>
            <p className="hd-dashboard-sub">
              Welcome back{user?.username ? `, ${user.username}` : ''}
            </p>
          </div>
          {propertyCredits && (
            <div className="hd-dashboard-credits">
              <span className="hd-dashboard-credits__label">Available credits</span>
              <HostelCreditsCell
                emailCredits={propertyCredits.emailCredits}
                whatsappCredits={propertyCredits.whatsappCredits}
              />
              <Link to="/credits" className="hd-dashboard-credits__link">
                View details →
              </Link>
            </div>
          )}
        </header>

        <div className="hd-kpi-row">
          {isVisible('dashboard.kpi_total_beds') && (
          <div className="hd-kpi-card">
            <div className="hd-kpi-icon hd-kpi-icon--blue">🛏️</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Total beds</span>
              <span className="hd-kpi-value">{kpi.totalBeds}</span>
              <span className="hd-kpi-meta">Across all floors</span>
            </div>
            <Link to="/rooms-beds" className="hd-kpi-link">
              Details →
            </Link>
          </div>
          )}
          {isVisible('dashboard.kpi_beds_available') && (
          <div className="hd-kpi-card">
            <div className="hd-kpi-icon hd-kpi-icon--cyan">✓</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Beds available</span>
              <span className="hd-kpi-value">{kpi.availableBeds}</span>
              <span className="hd-kpi-meta hd-kpi-meta--up">{kpi.occPct}% filled</span>
            </div>
            <Link to="/rooms-beds" className="hd-kpi-link">
              Details →
            </Link>
          </div>
          )}
          {isVisible('dashboard.kpi_beds_filled') && (
          <div className="hd-kpi-card">
            <div className="hd-kpi-icon hd-kpi-icon--indigo">👤</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Beds filled</span>
              <span className="hd-kpi-value">{kpi.occupiedBeds}</span>
              <span className="hd-kpi-meta hd-kpi-meta--up">Occupied</span>
            </div>
            <Link to="/tenants" className="hd-kpi-link">
              Details →
            </Link>
          </div>
          )}
          {isVisible('dashboard.kpi_notice_beds') && (
          <div className="hd-kpi-card">
            <div className="hd-kpi-icon hd-kpi-icon--amber">📋</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Notice beds</span>
              <span className="hd-kpi-value">{kpi.noticeBeds}</span>
              <span className="hd-kpi-meta">Vacating soon</span>
            </div>
            <Link to="/tenants" className="hd-kpi-link">
              Details →
            </Link>
          </div>
          )}
        </div>

        <div className="hd-kpi-row hd-kpi-row--finance">
          {isVisible('dashboard.kpi_collected_month') && (
          <div className="hd-kpi-card hd-kpi-card--highlight">
            <div className="hd-kpi-icon hd-kpi-icon--green">💵</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Collected this month</span>
              <span className="hd-kpi-value">₹{monthCollection.toLocaleString('en-IN')}</span>
              <span className="hd-kpi-meta">Rent &amp; payments received</span>
            </div>
            <Link to="/invoices" className="hd-kpi-link">
              Invoices →
            </Link>
          </div>
          )}
          {isVisible('dashboard.kpi_expected_collection') && (
          <div className="hd-kpi-card hd-kpi-card--expected">
            <div className="hd-kpi-icon hd-kpi-icon--indigo">📊</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Expected collection</span>
              <span className="hd-kpi-value">₹{monthlyExpected.amount.toLocaleString('en-IN')}</span>
              <span className="hd-kpi-meta">
                {monthlyExpected.tenantsWithRent} of {monthlyExpected.activeTenantCount} active tenants with rent
              </span>
            </div>
            <Link to="/reports/financials" className="hd-kpi-link">
              Reports →
            </Link>
          </div>
          )}
          {isVisible('dashboard.kpi_pending_payments') && (
          <div className="hd-kpi-card hd-kpi-card--warn">
            <div className="hd-kpi-icon hd-kpi-icon--rose">⏳</div>
            <div className="hd-kpi-body">
              <span className="hd-kpi-label">Pending payments</span>
              <span className="hd-kpi-value">₹{pendingPayments.totalAmount.toLocaleString('en-IN')}</span>
              <span className="hd-kpi-meta">
                {pendingPayments.tenantCount} tenant{pendingPayments.tenantCount === 1 ? '' : 's'} with balance
              </span>
            </div>
            <Link to="/reports/financials" className="hd-kpi-link">
              View all →
            </Link>
          </div>
          )}
        </div>

        <div className="hd-mid-row">
          {isVisible('dashboard.chart_collection_expenses') && (
          <section className="hd-panel hd-panel--chart">
            <div className="hd-panel-head">
              <div>
                <h2>Collection &amp; expenses</h2>
                <p className="hd-panel-sub">Month-wise growth · {dashboardYear}</p>
              </div>
              <Link to="/expenses" className="hd-panel-action">
                View expenses
              </Link>
            </div>
            <div className="hd-chart-wrap">
              <Bar
                data={growthChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top', align: 'end' },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `₹${Number(ctx.parsed.y || 0).toLocaleString('en-IN')}`,
                      },
                    },
                  },
                  scales: {
                    x: { grid: { display: false } },
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (v) => `₹${Number(v).toLocaleString('en-IN')}`,
                      },
                    },
                  },
                }}
              />
            </div>
            <div className="hd-chart-summary">
              <div className="hd-summary-box hd-summary-box--in">
                <span className="hd-summary-label">Money in (this month)</span>
                <span className="hd-summary-value">₹{monthCollection.toLocaleString('en-IN')}</span>
              </div>
              <div className="hd-summary-box hd-summary-box--out">
                <span className="hd-summary-label">Money out (this month)</span>
                <span className="hd-summary-value">₹{monthExpense.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </section>
          )}

          {isVisible('dashboard.empty_beds_by_floor') && (
          <section className="hd-panel hd-panel--floors">
            <div className="hd-panel-head">
              <h2>Empty beds by floor</h2>
            </div>
            <ul className="hd-floor-list">
              {emptyBedsByFloor.length === 0 ? (
                <li className="hd-floor-empty-msg">No floors configured yet.</li>
              ) : (
                emptyBedsByFloor.map((row, idx) => (
                  <li key={row.floor.id} className={idx === 0 ? 'is-highlight' : ''}>
                    <span className="hd-floor-icon" aria-hidden>
                      🪜
                    </span>
                    <div className="hd-floor-text">
                      <span className="hd-floor-name">{floorLabel(row.floor)}</span>
                      <span className="hd-floor-count">
                        {row.empty} empty · {row.total} beds
                      </span>
                    </div>
                    <span className="hd-floor-chevron" aria-hidden>
                      ›
                    </span>
                  </li>
                ))
              )}
            </ul>
            <Link to="/rooms-beds" className="hd-floor-footer-link">
              Manage rooms &amp; beds
            </Link>
          </section>
          )}
        </div>

        <div className="hd-bottom-row">
          {isVisible('dashboard.panel_pending_payments') && (
          <section className="hd-panel hd-panel--pending">
            <div className="hd-panel-head">
              <h2>Pending payments</h2>
              <Link to="/dues" className="hd-panel-action">
                Dues
              </Link>
            </div>
            {pendingPayments.top.length === 0 ? (
              <p className="hd-notice-empty">All tenants are up to date.</p>
            ) : (
              <ul className="hd-notice-list hd-pending-list">
                {pendingPayments.top.map((row) => (
                  <li key={row.tenant.id}>
                    <span className="hd-notice-avatar hd-notice-avatar--pending" aria-hidden>
                      {tenantDisplayName(row.tenant).charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <TenantNameLink
                        userId={row.tenant.id}
                        name={tenantDisplayName(row.tenant)}
                        className="hd-notice-name-link"
                      />
                      <span className="hd-notice-date">{tenantLocation(row.tenant, locationLookup)}</span>
                    </div>
                    <span className="hd-notice-badge hd-notice-badge--pending">{formatINR(row.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/reports/financials" className="hd-floor-footer-link">
              Full financial report
            </Link>
          </section>
          )}

          {isVisible('dashboard.panel_tenants_vacating_today') && (
          <section className="hd-panel hd-panel--vacating-today">
            <div className="hd-panel-head">
              <h2>Vacating today</h2>
              <Link to="/tenants" className="hd-panel-action">
                Tenants
              </Link>
            </div>
            {tenantsVacatingToday.length === 0 ? (
              <p className="hd-notice-empty">No tenants vacating today.</p>
            ) : (
              <ul className="hd-notice-list">
                {tenantsVacatingToday.map((n) => (
                  <li key={n.id}>
                    <span className="hd-notice-avatar hd-notice-avatar--vacating-today" aria-hidden>
                      {n.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <TenantNameLink userId={n.id} name={n.name} className="hd-notice-name-link" />
                      <span className="hd-notice-date">Leaving: {n.leaving}</span>
                      {n.notified !== '—' ? (
                        <span className="hd-notice-notified">Notified: {n.notified}</span>
                      ) : null}
                    </div>
                    <span className="hd-notice-badge hd-notice-badge--today">Today</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {isVisible('dashboard.panel_upcoming_vacating') && (
          <section className="hd-panel hd-panel--vacating">
            <div className="hd-panel-head">
              <h2>Upcoming vacating users</h2>
              <Link to="/tenants" className="hd-panel-action">
                Vacating users
              </Link>
            </div>
            {upcomingVacatingUsers.length === 0 ? (
              <p className="hd-notice-empty">No upcoming vacating users.</p>
            ) : (
              <ul className="hd-notice-list">
                {upcomingVacatingUsers.map((n) => (
                  <li key={n.id}>
                    <span className="hd-notice-avatar" aria-hidden>
                      {n.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <TenantNameLink userId={n.id} name={n.name} className="hd-notice-name-link" />
                      <span className="hd-notice-date">Vacating: {n.leaving}</span>
                      {n.notified !== '—' && (
                        <span className="hd-notice-notified">Notified: {n.notified}</span>
                      )}
                    </div>
                    <span className="hd-notice-badge">Upcoming</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {isVisible('dashboard.panel_guests_checkout_today') && (
          <section className="hd-panel hd-panel--guest-checkout">
            <div className="hd-panel-head">
              <h2>Guest checkout today</h2>
              <Link to="/daily-guests" className="hd-panel-action">
                Daily guests
              </Link>
            </div>
            {guestsCheckingOutToday.length === 0 ? (
              <p className="hd-notice-empty">No guests checking out today.</p>
            ) : (
              <ul className="hd-notice-list hd-guest-list">
                {guestsCheckingOutToday.map((row) => (
                  <li key={row.id} className="hd-guest-row">
                    <span className="hd-notice-avatar hd-notice-avatar--guest" aria-hidden>
                      {row.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <span className="hd-notice-name">{row.name}</span>
                      <span className="hd-notice-date">{row.location}</span>
                      <span className="hd-notice-notified">
                        {row.checkIn} → {row.checkOut}
                        {row.phone ? (
                          <>
                            {' · '}
                            <PhoneLink phone={row.phone} />
                          </>
                        ) : null}
                      </span>
                    </div>
                    <div className="hd-guest-row__actions">
                      {row.balance > 0 ? (
                        <span className="hd-notice-badge hd-notice-badge--pending">
                          {formatINR(row.balance)}
                        </span>
                      ) : (
                        <span className="hd-notice-badge hd-notice-badge--guest-paid">Paid</span>
                      )}
                      <button
                        type="button"
                        className="hd-guest-checkout-btn"
                        disabled={guestCheckoutBusyId === row.id}
                        onClick={() => void handleGuestCheckout(row.guest)}
                      >
                        {guestCheckoutBusyId === row.id ? '…' : 'Check out'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {isVisible('dashboard.panel_guests_upcoming_checkout') && (
          <section className="hd-panel hd-panel--guest-upcoming">
            <div className="hd-panel-head">
              <h2>Upcoming guest check-outs</h2>
              <Link to="/daily-guests" className="hd-panel-action">
                Daily guests
              </Link>
            </div>
            {upcomingGuestCheckouts.length === 0 ? (
              <p className="hd-notice-empty">No upcoming guest check-outs in the next 14 days.</p>
            ) : (
              <ul className="hd-notice-list">
                {upcomingGuestCheckouts.map((row) => (
                  <li key={row.id}>
                    <span className="hd-notice-avatar hd-notice-avatar--guest" aria-hidden>
                      {row.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <span className="hd-notice-name">{row.name}</span>
                      <span className="hd-notice-date">Check-out: {row.checkOut}</span>
                      {row.balance > 0 ? (
                        <span className="hd-notice-notified">Due: {formatINR(row.balance)}</span>
                      ) : null}
                    </div>
                    <span className="hd-notice-badge">Upcoming</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {isVisible('dashboard.panel_guests_checkin_today') && (
          <section className="hd-panel hd-panel--guest-checkin">
            <div className="hd-panel-head">
              <h2>Guest check-in today</h2>
              <Link to="/daily-guests" className="hd-panel-action">
                Daily guests
              </Link>
            </div>
            {guestsCheckingInToday.length === 0 ? (
              <p className="hd-notice-empty">No guests checking in today.</p>
            ) : (
              <ul className="hd-notice-list">
                {guestsCheckingInToday.map((row) => (
                  <li key={row.id}>
                    <span className="hd-notice-avatar hd-notice-avatar--guest-checkin" aria-hidden>
                      {row.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <span className="hd-notice-name">{row.name}</span>
                      <span className="hd-notice-date">{row.location}</span>
                      <span className="hd-notice-notified">Until {row.checkOut}</span>
                    </div>
                    <span className="hd-notice-badge hd-notice-badge--guest-checkin">Arriving</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {isVisible('dashboard.panel_joined_month') && (
          <section className="hd-panel hd-panel--joined">
            <div className="hd-panel-head">
              <div>
                <h2>Joined this month</h2>
                <p className="hd-panel-sub">{joinedThisMonthLabel}</p>
              </div>
              <Link to="/tenants" className="hd-panel-action">
                All users
              </Link>
            </div>
            {joinedThisMonthUsers.length === 0 ? (
              <p className="hd-notice-empty">No users joined this month yet.</p>
            ) : (
              <ul className="hd-notice-list hd-joined-list">
                {joinedThisMonthUsers.map((u) => (
                  <li key={u.id}>
                    <span className="hd-notice-avatar hd-notice-avatar--joined" aria-hidden>
                      {u.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="hd-notice-text">
                      <TenantNameLink userId={u.id} name={u.name} className="hd-notice-name-link" />
                      <span className="hd-notice-date">Joined: {u.joined}</span>
                      {u.phone ? (
                        <span className="hd-notice-notified">
                          <PhoneLink phone={u.phone} />
                        </span>
                      ) : null}
                    </div>
                    <span className="hd-notice-badge hd-notice-badge--joined">New</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}

          {isVisible('dashboard.chart_rent_status') && (
          <section className="hd-panel hd-panel--pie">
            <div className="hd-panel-head">
              <h2>Tenant rent status</h2>
              <p className="hd-panel-sub">Paid vs unpaid · current month</p>
            </div>
            <div className="hd-pie-wrap">
              <div className="hd-pie-chart">
                <Pie
                  data={bookingPieData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    cutout: '62%',
                    plugins: { legend: { display: false } },
                  }}
                />
                <div className="hd-pie-center">
                  <span className="hd-pie-center-value">{paidUsersPie.total}</span>
                  <span className="hd-pie-center-label">Users</span>
                </div>
              </div>
              <ul className="hd-pie-legend">
                <li>
                  <span className="hd-pie-dot hd-pie-dot--paid" />
                  Paid this month
                  <strong>{paidUsersPie.paid}</strong>
                </li>
                <li>
                  <span className="hd-pie-dot hd-pie-dot--pending" />
                  Not paid yet
                  <strong>{paidUsersPie.pending}</strong>
                </li>
              </ul>
            </div>
            <Link to="/invoices" className="hd-pie-cta">
              View invoices
            </Link>
          </section>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <h1 className="dashboard-title">Dashboard</h1>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📄</div>
          <div className="stat-content">
            <h3>Total Invoices</h3>
            <p className="stat-value">{stats.totalInvoices}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Total Paid</h3>
            <p className="stat-value">₹{stats.totalRevenue.toLocaleString()}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Pending</h3>
            <p className="stat-value">₹{stats.pendingPayments.toLocaleString()}</p>
          </div>
        </div>
      </div>
      <div className="recent-section">
        <h2>Recent Invoices</h2>
        <div className="invoices-table">
          <table>
            <thead>
              <tr>
                <th>Invoice ID</th>
                <th>Month/Year</th>
                <th>Amount</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {recentInvoices.length === 0 ? (
                <tr>
                  <td colSpan={4} className="no-data">
                    No invoices found
                  </td>
                </tr>
              ) : (
                recentInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>#{invoice.id}</td>
                    <td>
                      {invoice.paymentMonth}/{invoice.paymentYear}
                    </td>
                    <td>₹{parseFloat(String(invoice.paidAmount || '0')).toLocaleString()}</td>
                    <td>₹{parseFloat(String(invoice.balanceAmount || '0')).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
